import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { Loader } from './components'
import Layout from './components/layout'
import CompleteSignup from './pages/complete-signup'
import Error404 from './pages/error-404'
import { publicRoutes, routes, unauthenticatedRoutes } from './routes/index'

import { checkForUserBranch } from './helpers'
import { trackEvent } from './helpers/analytics'
import { hasOnboardingDone } from './helpers/employee'
import { ONBOARDING_SKIP_ROUTE, skipsOnboarding } from './helpers/experiment'
import { localStorage } from './helpers/local-storage'

import AccessDenied from './components/access-denied'
import ReleaseNotes from './components/release-notes'
import StartupHandler from './components/startup-handler'
import Trial from './components/trial/trial'
import trackEvents from './constants/track-events'
import { DataContext, GeneralContext } from './context'
import { useActiveSpaceQuery } from './hooks/use-active-space-query'
import { useEmployeesQuery } from './hooks/use-employees-query'
import { useMount } from './hooks/use-mount'
// import Checkout from './pages/checkout'
import { usePartnerQuery } from './hooks/use-partner-query'
import { useUserQuery } from './hooks/use-user-query'
// import Checkout from './pages/checkout' //
import { DiscoverIntroPage, DiscoverPage } from './features/discover'
import { useAdminOnboardingSkip } from './features/discover/admin-onboarding-skip'
import CreateCareerFlow from './features/discover/pages/CreateCareerFlow'
import { usePaymentsQuery } from './hooks/use-payments-query'
import { useSystemQuery } from './hooks/use-system-query'

function App({ language, cognitoUser, restartDashboard }) {
  const { t } = useTranslation()

  useMount(() => {
    checkForUserBranch()
    trackEvent(trackEvents.LAUNCH)
  })

  const username = useMemo(() => {
    if (!cognitoUser) return null

    return cognitoUser.username
  }, [cognitoUser])

  const cognitoGroups = useMemo(() => {
    if (!cognitoUser) return []

    const { payload } = cognitoUser.signInUserSession.accessToken
    const groups = [...(payload['cognito:groups'] || [])]

    return groups
  }, [cognitoUser])

  const isAdmin = useMemo(
    () => cognitoGroups.includes('__admin'),
    [cognitoGroups]
  )

  const partner_id = useMemo(() => {
    const active_partner_id_from_groups = cognitoGroups.find((g) =>
      g.includes('__active_partner_id')
    )

    if (!active_partner_id_from_groups) return

    return (
      localStorage.getItem('admin') ||
      active_partner_id_from_groups.split(':').pop()
    )
  }, [cognitoGroups])

  const active_space_id = useMemo(() => {
    const active_space_id_from_groups = cognitoGroups.find((g) =>
      g.includes('__active_space_id')
    )

    if (!active_space_id_from_groups) return

    return active_space_id_from_groups.split(':').pop()
  }, [cognitoGroups])

  const { data: activeSpace, refetch: refetchActiveSpace } =
    useActiveSpaceQuery({
      active_space_id
    })

  const { data: user } = useUserQuery({
    username
  })

  const { data: employees, refetch: refetchEmployees } = useEmployeesQuery({
    username
  })

  const { data: partner, refetch: refetchPartner } = usePartnerQuery({
    partner_id
  })

  const { data: system } = useSystemQuery({ username })

  const { data: payments, accessDenied } = usePaymentsQuery({
    partner_id,
    active_space_id,
    cognitoUser
  })

  // Rückweg aus der Sackgasse: hat der User neben dem aktuellen (unbezahlten)
  // Space weitere EMPLOYEE-Spaces, bekommt er sie auf dem AccessDenied-Screen
  // zur Auswahl. Ohne weitere Spaces bleibt der Screen wie er ist — der
  // legitime Fall des abgelaufenen Kunden.
  const accessDeniedFallbackSpaces = useMemo(
    () => (employees || []).filter(({ id }) => id !== active_space_id),
    [employees, active_space_id]
  )

  const isDiscoverVisible =
    partner?.app_settings &&
    JSON.parse(partner.app_settings.dashboard_settings || '{}')?.experimental
      ?.components?.discover === true
  // Admin-Skip wie im UserContext einrechnen, sonst schickt das Experiment
  // onboarding_skip den Admin trotzdem in den Onboarding-Pfad
  const { skipped: adminSkippedOnboarding } = useAdminOnboardingSkip({
    isAdmin
  })
  const hasCompletedOnboarding = useMemo(() => {
    if (adminSkippedOnboarding) return true
    if (!activeSpace?.customer_dashboard_meta) return false
    try {
      // Prüfung liegt in helpers/employee.js, weil der UserContext sie ebenfalls
      // braucht. Hier muss selbst geparst werden: der Context existiert an
      // dieser Stelle noch nicht.
      return hasOnboardingDone(JSON.parse(activeSpace.customer_dashboard_meta))
    } catch {
      return false
    }
  }, [activeSpace?.customer_dashboard_meta, adminSkippedOnboarding])

  // Experiment onboarding_skip (Rock Q3/26): Variante B landet direkt auf
  // „Stelle erstellen" statt im geführten Onboarding. Die Zuteilung ist
  // deterministisch aus partner.id — hier steht partner garantiert (Render-Gate
  // unten wartet auf !partner), also kein Flicker und kein Umspringen.
  const skipOnboarding = skipsOnboarding(partner?.id, hasCompletedOnboarding)
  const homeRoute = !isDiscoverVisible
    ? '/talents'
    : skipOnboarding
      ? ONBOARDING_SKIP_ROUTE
      : '/discover/'

  // Public routes — rendered before any auth check (iframe-safe)
  // Trailing Slash wird abgeschnitten: der Vergleich ist exakt, und wer eine
  // Doku-URL weitergibt, schreibt sie schnell als /api-docs/ — ohne das hier
  // liefe der Aufruf in den Auth-Zweig statt auf die oeffentliche Seite.
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/'
  // matchPath statt ===, damit auch Routen mit Parametern (/product/updates/:slug)
  // oeffentlich sind. Fuer statische Pfade verhaelt es sich wie der exakte Vergleich.
  const isPublicRoute = publicRoutes.some(({ path }) =>
    matchPath({ path, end: true }, currentPath)
  )
  if (isPublicRoute) {
    return (
      <Routes>
        {publicRoutes.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
      </Routes>
    )
  }

  if (accessDenied) {
    return (
      <AccessDenied
        spaces={accessDeniedFallbackSpaces}
        userId={username}
        restartDashboard={restartDashboard}
      />
    )
  }

  // show authenticator if !cognitoUser
  const isLoading = cognitoUser
    ? !activeSpace ||
      !system ||
      !partner ||
      !payments?.length ||
      !employees ||
      !user
    : false

  if (isLoading) {
    return <Loader loadingText={t('app.dashboard_loading_text')} />
  }

  if (!cognitoUser) {
    return (
      <GeneralContext language={language}>
        <Routes>
          {unauthenticatedRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          <Route
            path='/ausprobieren'
            element={<Navigate replace to='/funnel' />}
          />
          <Route
            path='/de/ausprobieren'
            element={<Navigate replace to='/funnel' />}
          />
          <Route path='/en/try' element={<Navigate replace to='/funnel' />} />
          <Route
            path='/ausprobieren/:id'
            element={<Navigate replace to='/funnel' />}
          />
        </Routes>
      </GeneralContext>
    )
  }

  return (
    <GeneralContext language={language}>
      <DataContext
        system={system}
        restartDashboard={restartDashboard}
        cognitoUser={{ ...cognitoUser, groups: cognitoGroups, partner_id }}
        activeSpace={activeSpace}
        refetchActiveSpace={refetchActiveSpace}
        isAdmin={isAdmin}
        partner={partner}
        refetchPartner={refetchPartner}
        payments={payments}
        employees={employees}
        refetchEmployees={refetchEmployees}
        user={user}
      >
        <StartupHandler />
        <Routes>
          {/* <Route path='/ausprobieren' element={<Tryout />} /> */}
          <Route path='/' element={<Navigate to={homeRoute} replace />} />
          <Route
            path='/login'
            element={
              <Navigate
                to={skipOnboarding ? ONBOARDING_SKIP_ROUTE : '/discover'}
                replace
              />
            }
          />

          <Route
            path='/discover'
            element={
              <Layout>
                {/* bannerOnly: Trial-Banner ja, Tutorials/Invite-Modal nein */}
                <Trial bannerOnly />
                <Outlet />
                <ReleaseNotes startup asPopup />
              </Layout>
            }
          >
            <Route index element={<DiscoverPage />} />
            <Route path='intro' element={<DiscoverIntroPage />} />

            <Route path='create-career' element={<CreateCareerFlow />} />
          </Route>

          {routes.map(
            ({ path, Component, useLayout, fillViewport, children }) => (
              <Route
                key={path}
                path={path}
                element={
                  useLayout ? (
                    <Layout fillViewport={!!fillViewport}>
                      <Trial />
                      <Component />
                      <ReleaseNotes startup asPopup />
                      {/* <ProductTour /> */}
                    </Layout>
                  ) : (
                    <Component />
                  )
                }
              >
                {children?.map((child) => (
                  <Route
                    key={child.index ? 'index' : child.path}
                    index={child.index}
                    path={child.path}
                    element={<child.Component />}
                  />
                ))}
              </Route>
            )
          )}

          <Route path='/complete-signup' element={<CompleteSignup />} />

          <Route
            path='/ausprobieren'
            element={<Navigate replace to='/careers' />}
          />
          <Route path='*' element={<Error404 />} />
        </Routes>
      </DataContext>
    </GeneralContext>
  )
}

App.propTypes = {
  language: PropTypes.string.isRequired,
  cognitoUser: PropTypes.object,
  restartDashboard: PropTypes.func.isRequired
}

App.defaultProps = {
  cognitoUser: null
}

export default App
